// @ts-nocheck
/**
 * Role Assignment Interface
 * Epic #17: RBAC & Permissions Platform Services
 *
 * Comprehensive interface for assigning and managing user roles
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Column,
  Tile,
  Search,
  Dropdown,
  ComboBox,
  DatePicker,
  DatePickerInput,
  Button,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tag,
  Modal,
  FileUploader,
  InlineNotification,
  TextInput,
  TextArea,
  Loading,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@carbon/react';
import { UserRole, Add, TrashCan, Upload, Download, CheckmarkFilled } from '@carbon/icons-react';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore';
import type {
  EducationalRole,
  InstitutionalContext,
  UserRoleAssignment,
} from '../../../../shared/interfaces/rbac.interface';
import styles from './RoleAssignmentPage.module.scss';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// Educational roles for dropdown
const EDUCATIONAL_ROLES = [
  { id: 'student', label: 'Student' },
  { id: 'parent', label: 'Parent/Guardian' },
  { id: 'teacher', label: 'Teacher' },
  { id: 'principal', label: 'Principal' },
  { id: 'district_admin', label: 'District Administrator' },
  { id: 'creator', label: 'Content Creator' },
  { id: 'platform_admin', label: 'Platform Administrator' },
];

// Institution types
const INSTITUTION_TYPES = [
  { id: 'school', label: 'School' },
  { id: 'district', label: 'District' },
  { id: 'class', label: 'Class/Classroom' },
  { id: 'platform', label: 'Platform-wide' },
];

interface User {
  user_id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
}

interface BulkAssignmentError {
  row: number;
  email?: string;
  role?: string;
  error: string;
}

const RoleAssignmentPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, token } = useAuthStore();

  // Single assignment state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [institutionType, setInstitutionType] = useState<string>('platform');
  const [institutionId, setInstitutionId] = useState('PLATFORM-001');
  const [institutionName, setInstitutionName] = useState('Sigma08 Platform');
  const [effectiveDate, setEffectiveDate] = useState<Date>(new Date());
  const [expirationDate, setExpirationDate] = useState<Date | null>(null);
  const [assignmentReason, setAssignmentReason] = useState('');

  // User's current roles state
  const [userRoles, setUserRoles] = useState<UserRoleAssignment[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  // Bulk upload state
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [bulkResults, setBulkResults] = useState<any>(null);
  const [bulkErrors, setBulkErrors] = useState<BulkAssignmentError[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<{
    kind: 'success' | 'error' | 'warning' | 'info';
    title: string;
    subtitle: string;
  } | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Search users by email
  const handleSearch = async (value: string) => {
    setSearchQuery(value);

    if (value.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/v1/admin/users`, {
        params: { search: value, per_page: 10 },
        headers: { Authorization: `Bearer ${token}` },
      });

      setSearchResults(response.data.users || []);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    }
  };

  // Select user from search results
  const handleSelectUser = async (user: User) => {
    setSelectedUser(user);
    setSearchResults([]);
    setSearchQuery(user.email);

    // Load user's current role assignments
    await loadUserRoles(user.user_id);
  };

  // Load user's current role assignments
  const loadUserRoles = async (userId: string) => {
    setLoadingRoles(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/v1/rbac/users/${userId}/assignments`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setUserRoles(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading user roles:', error);
      setUserRoles([]);
    } finally {
      setLoadingRoles(false);
    }
  };

  // Assign role
  const handleAssignRole = async () => {
    if (!selectedUser || !selectedRole) {
      setNotification({
        kind: 'warning',
        title: 'Missing Information',
        subtitle: 'Please select a user and role',
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/v1/rbac/assign-role`,
        {
          user_id: selectedUser.user_id,
          role_id: selectedRole,
          institutional_context: {
            institution_type: institutionType,
            institution_id: institutionId,
            institution_name: institutionName,
          },
          effective_date: effectiveDate.toISOString(),
          expiration_date: expirationDate ? expirationDate.toISOString() : null,
          assignment_reason: assignmentReason || undefined,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success || response.data.assignment_id) {
        setNotification({
          kind: 'success',
          title: 'Role Assigned',
          subtitle: `Successfully assigned ${selectedRole} to ${selectedUser.email}`,
        });

        // Reload user roles
        await loadUserRoles(selectedUser.user_id);

        // Reset form
        setSelectedRole('');
        setAssignmentReason('');
        setShowConfirmModal(false);
      }
    } catch (error: any) {
      setNotification({
        kind: 'error',
        title: 'Assignment Failed',
        subtitle: error.response?.data?.message || error.message || 'Failed to assign role',
      });
    } finally {
      setLoading(false);
    }
  };

  // Revoke role
  const handleRevokeRole = async (assignmentId: string) => {
    if (!confirm('Are you sure you want to revoke this role assignment?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/v1/rbac/revoke-role`,
        { assignment_id: assignmentId },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setNotification({
          kind: 'success',
          title: 'Role Revoked',
          subtitle: 'Role assignment successfully revoked',
        });

        // Reload user roles
        if (selectedUser) {
          await loadUserRoles(selectedUser.user_id);
        }
      }
    } catch (error: any) {
      setNotification({
        kind: 'error',
        title: 'Revocation Failed',
        subtitle: error.response?.data?.message || error.message || 'Failed to revoke role',
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle CSV file upload
  const handleCsvUpload = (event: any) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setCsvFile(files[0]);
    }
  };

  // Process CSV bulk assignment
  const handleBulkAssign = async () => {
    if (!csvFile) {
      setNotification({
        kind: 'warning',
        title: 'No File Selected',
        subtitle: 'Please select a CSV file to upload',
      });
      return;
    }

    setLoading(true);
    setBulkResults(null);
    setBulkErrors([]);

    try {
      // Read and parse CSV file
      const text = await csvFile.text();
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim());

      // Validate headers
      if (!headers.includes('email') || !headers.includes('role_name')) {
        throw new Error('CSV must contain at least "email" and "role_name" columns');
      }

      // Parse CSV rows
      const assignments = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim());
        const row: any = {};
        headers.forEach((header, i) => {
          row[header] = values[i] || '';
        });
        return row;
      });

      // Send bulk assignment request
      const response = await axios.post(
        `${API_BASE_URL}/v1/rbac/bulk-assign`,
        { assignments },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setBulkResults(response.data.data);

      if (response.data.success) {
        setNotification({
          kind: 'success',
          title: 'Bulk Assignment Complete',
          subtitle: `Successfully assigned ${response.data.data.succeeded} roles`,
        });
      } else {
        setBulkErrors(response.data.data.errors || []);
        setNotification({
          kind: 'warning',
          title: 'Partial Success',
          subtitle: `${response.data.data.succeeded} succeeded, ${response.data.data.failed} failed`,
        });
      }
    } catch (error: any) {
      setNotification({
        kind: 'error',
        title: 'Bulk Assignment Failed',
        subtitle: error.response?.data?.message || error.message || 'Failed to process CSV file',
      });
    } finally {
      setLoading(false);
    }
  };

  // Download CSV template
  const handleDownloadTemplate = () => {
    const template = `email,role_name,institution_type,institution_id,institution_name,effective_date,expiration_date,assignment_reason
user@example.com,teacher,school,SCHOOL-001,Example School,${new Date().toISOString()},,Initial assignment
`;
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'role_assignment_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Table headers for user roles
  const roleTableHeaders = [
    { key: 'role_name', header: 'Role' },
    { key: 'institution', header: 'Institution' },
    { key: 'effective_date', header: 'Effective Date' },
    { key: 'expiration_date', header: 'Expiration' },
    { key: 'actions', header: 'Actions' },
  ];

  // Prepare role table rows
  const getRoleTableRows = () => {
    return userRoles.map((assignment, index) => ({
      id: `${index}`,
      assignment_id: assignment.assignment_id,
      role_name: assignment.role_id,
      institution: `${assignment.institutional_context.institution_name} (${assignment.institutional_context.institution_type})`,
      effective_date: new Date(assignment.effective_date).toLocaleDateString(),
      expiration_date: assignment.expiration_date ? new Date(assignment.expiration_date).toLocaleDateString() : 'Never',
    }));
  };

  return (
    <Grid fullWidth className={styles.roleAssignmentPage}>
      {/* Header */}
      <Column lg={16}>
        <div className={styles.pageHeader}>
          <div>
            <h1>Role Assignment</h1>
            <p>Assign and manage user roles across the platform</p>
          </div>
          <Button kind="ghost" onClick={() => navigate('/admin/rbac')} className={styles.marginLeftAuto}>
            Back to Dashboard
          </Button>
        </div>
      </Column>

      {/* Notifications */}
      {notification && (
        <Column lg={16}>
          <InlineNotification
            kind={notification.kind}
            title={notification.title}
            subtitle={notification.subtitle}
            onCloseButtonClick={() => setNotification(null)}
            className={styles.marginBottomSmall}
          />
        </Column>
      )}

      {/* Tabs */}
      <Column lg={16}>
        <Tabs selectedIndex={activeTab} onChange={e => setActiveTab(e.selectedIndex)}>
          <TabList aria-label="Assignment methods">
            <Tab>Single Assignment</Tab>
            <Tab>Bulk Upload</Tab>
          </TabList>
          <TabPanels>
            {/* Single Assignment Tab */}
            <TabPanel>
              <Grid>
                {/* User Search */}
                <Column lg={16}>
                  <Tile className={styles.sectionTile}>
                    <h3>1. Find User</h3>
                    <Search
                      labelText="Search by email or ID"
                      placeholder="Search for users..."
                      value={searchQuery}
                      onChange={e => handleSearch(e.target.value)}
                      onClear={() => {
                        setSearchQuery('');
                        setSearchResults([]);
                        setSelectedUser(null);
                        setUserRoles([]);
                      }}
                      size="lg"
                    />
                    {searchResults.length > 0 && (
                      <div className={styles.searchResults}>
                        {searchResults.map(user => (
                          <div
                            key={user.user_id}
                            onClick={() => handleSelectUser(user)}
                            className={styles.searchResultItem}
                          >
                            <strong>{user.email}</strong>
                            {user.first_name && user.last_name && (
                              <span className={styles.userDetails}>
                                {user.first_name} {user.last_name}
                              </span>
                            )}
                            <Tag type="blue" size="sm" className={styles.userTag}>
                              {user.role}
                            </Tag>
                          </div>
                        ))}
                      </div>
                    )}
                    {selectedUser && (
                      <div className={styles.selectedUser}>
                        <CheckmarkFilled size={20} className={styles.successIcon} />
                        <strong>Selected:</strong> {selectedUser.email}
                        {selectedUser.first_name && selectedUser.last_name && (
                          <span className={styles.userName}>
                            ({selectedUser.first_name} {selectedUser.last_name})
                          </span>
                        )}
                      </div>
                    )}
                  </Tile>
                </Column>

                {/* Current Roles */}
                {selectedUser && (
                  <Column lg={16}>
                    <Tile className={styles.sectionTile}>
                      <h3>Current Role Assignments</h3>
                      {loadingRoles ? (
                        <Loading description="Loading roles..." withOverlay={false} />
                      ) : userRoles.length === 0 ? (
                        <p className={styles.secondaryText}>No active role assignments</p>
                      ) : (
                        <DataTable rows={getRoleTableRows()} headers={roleTableHeaders} size="md">
                          {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                            <TableContainer>
                              <Table {...getTableProps()}>
                                <TableHead>
                                  <TableRow>
                                    {headers.map(header => (
                                      <TableHeader {...getHeaderProps({ header })} key={header.key}>
                                        {header.header}
                                      </TableHeader>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {rows.map(row => (
                                    <TableRow {...getRowProps({ row })} key={row.id}>
                                      {row.cells.map(cell => (
                                        <TableCell key={cell.id}>
                                          {cell.info.header === 'actions' ? (
                                            <Button
                                              kind="danger--ghost"
                                              size="sm"
                                              renderIcon={TrashCan}
                                              onClick={() => handleRevokeRole(row.cells[0].value.assignment_id)}
                                            >
                                              Revoke
                                            </Button>
                                          ) : (
                                            cell.value
                                          )}
                                        </TableCell>
                                      ))}
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                        </DataTable>
                      )}
                    </Tile>
                  </Column>
                )}

                {/* Role Selection */}
                {selectedUser && (
                  <Column lg={16}>
                    <Tile className={styles.sectionTile}>
                      <h3>2. Assign New Role</h3>
                      <Grid>
                        <Column lg={8}>
                          <Dropdown
                            id="role-dropdown"
                            titleText="Select Role"
                            label="Choose a role"
                            items={EDUCATIONAL_ROLES}
                            itemToString={item => item.label}
                            onChange={e => setSelectedRole(e.selectedItem.id)}
                            selectedItem={EDUCATIONAL_ROLES.find(r => r.id === selectedRole)}
                          />
                        </Column>
                        <Column lg={8}>
                          <Dropdown
                            id="institution-type-dropdown"
                            titleText="Institution Type"
                            label="Select type"
                            items={INSTITUTION_TYPES}
                            itemToString={item => item.label}
                            onChange={e => setInstitutionType(e.selectedItem.id)}
                            selectedItem={INSTITUTION_TYPES.find(t => t.id === institutionType)}
                          />
                        </Column>
                        <Column lg={8}>
                          <TextInput
                            id="institution-id"
                            labelText="Institution ID"
                            value={institutionId}
                            onChange={e => setInstitutionId(e.target.value)}
                          />
                        </Column>
                        <Column lg={8}>
                          <TextInput
                            id="institution-name"
                            labelText="Institution Name"
                            value={institutionName}
                            onChange={e => setInstitutionName(e.target.value)}
                          />
                        </Column>
                        <Column lg={16}>
                          <TextArea
                            id="assignment-reason"
                            labelText="Assignment Reason (Optional)"
                            placeholder="Enter reason for this role assignment..."
                            value={assignmentReason}
                            onChange={e => setAssignmentReason(e.target.value)}
                            rows={3}
                          />
                        </Column>
                        <Column lg={16} className={styles.marginTopSmall}>
                          <Button
                            kind="primary"
                            renderIcon={Add}
                            onClick={() => setShowConfirmModal(true)}
                            disabled={!selectedRole || loading}
                          >
                            Assign Role
                          </Button>
                        </Column>
                      </Grid>
                    </Tile>
                  </Column>
                )}
              </Grid>
            </TabPanel>

            {/* Bulk Upload Tab */}
            <TabPanel>
              <Grid>
                <Column lg={16}>
                  <Tile className={styles.sectionTile}>
                    <h3>Bulk Role Assignment via CSV</h3>
                    <p className={styles.bulkDescription}>
                      Upload a CSV file with user emails and role assignments. Download the template to get started.
                    </p>
                    <Button
                      kind="tertiary"
                      renderIcon={Download}
                      onClick={handleDownloadTemplate}
                      className={styles.marginBottomSmall}
                    >
                      Download CSV Template
                    </Button>
                    <FileUploader
                      labelTitle="Upload CSV File"
                      labelDescription="Maximum file size is 5MB. Only CSV files are supported."
                      buttonLabel="Select file"
                      accept={['.csv']}
                      onChange={handleCsvUpload}
                      filenameStatus="edit"
                    />
                    {csvFile && (
                      <div className={styles.fileInfo}>
                        <p>
                          <strong>Selected file:</strong> {csvFile.name}
                        </p>
                        <Button
                          kind="primary"
                          renderIcon={Upload}
                          onClick={handleBulkAssign}
                          disabled={loading}
                          className={styles.marginTopSmall}
                        >
                          Process Bulk Assignment
                        </Button>
                      </div>
                    )}
                  </Tile>
                </Column>

                {/* Bulk Results */}
                {bulkResults && (
                  <Column lg={16}>
                    <Tile className={styles.sectionTile}>
                      <h3>Bulk Assignment Results</h3>
                      <Grid>
                        <Column lg={5}>
                          <p>
                            <strong>Total:</strong> {bulkResults.total}
                          </p>
                        </Column>
                        <Column lg={5}>
                          <p className={styles.successText}>
                            <strong>Succeeded:</strong> {bulkResults.succeeded}
                          </p>
                        </Column>
                        <Column lg={6}>
                          <p className={styles.errorText}>
                            <strong>Failed:</strong> {bulkResults.failed}
                          </p>
                        </Column>
                      </Grid>
                      {bulkErrors.length > 0 && (
                        <div className={styles.bulkResults}>
                          <h4>Errors</h4>
                          <div className={styles.errorList}>
                            {bulkErrors.map((error, index) => (
                              <InlineNotification
                                key={index}
                                kind="error"
                                title={`Row ${error.row}`}
                                subtitle={`${error.email ? `${error.email} - ` : ''}${error.error}`}
                                lowContrast
                                hideCloseButton
                                className={styles.errorItem}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </Tile>
                  </Column>
                )}
              </Grid>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Column>

      {/* Confirmation Modal */}
      <Modal
        open={showConfirmModal}
        onRequestClose={() => setShowConfirmModal(false)}
        modalHeading="Confirm Role Assignment"
        primaryButtonText="Assign Role"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleAssignRole}
        danger={false}
      >
        <div className={styles.modalContent}>
          <p>
            You are about to assign the <strong>{selectedRole}</strong> role to <strong>{selectedUser?.email}</strong>{' '}
            in the context of{' '}
            <strong>
              {institutionName} ({institutionType})
            </strong>
            .
          </p>
          {assignmentReason && (
            <p className={styles.modalReason}>
              <strong>Reason:</strong> {assignmentReason}
            </p>
          )}
          <p className={styles.modalWarning}>
            This action will be logged in the audit trail. Are you sure you want to proceed?
          </p>
        </div>
      </Modal>

      {/* Loading Overlay */}
      {loading && <Loading description="Processing..." withOverlay={true} />}
    </Grid>
  );
};

export default RoleAssignmentPage;
